import React from "react";
import PropTypes from "prop-types";
import balanceAppTracking from "./BalanceAppTracking";

const MainApp = ({
  user,
  error,
  setError,
  urlInput,
  setUrlInput,
  loading,
  opposingViewpointsEnabled,
  setOpposingViewpointsEnabled,
  handleSubmit,
  renderBiasInfo,
  renderSuggestedArticles,
  message,
  setMessage,
  renderUserInfo,
}) => {
  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Error Banner */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-red-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="bg-white shadow-sm border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-6 py-3">
          <div className="text-center">
            <div className="flex items-center justify-center mb-4">
              <a
                href="/"
                className="hover:opacity-80 transition-opacity duration-200"
              >
                <img
                  src="/logo-full.png"
                  alt="Not My View Logo"
                  className="h-16 sm:h-20 md:h-24 w-auto max-w-full cursor-pointer"
                />
              </a>
            </div>
            <p className="text-lg text-neutral-600">
              Check news article bias and credibility with AI-powered analysis
            </p>
          </div>
        </div>
      </header>

      {/* User Info Bar */}
      {renderUserInfo()}

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-8 space-y-8">
        {/* Search Form */}
        <div className="bg-white rounded-xl shadow-lg border border-neutral-200 p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label
                htmlFor="url"
                className="block text-sm font-medium text-neutral-700 mb-2"
              >
                News Article URL or Search Topic
              </label>
              <div className="relative">
                <input
                  type="text"
                  id="url"
                  value={urlInput}
                  onChange={(e) => setUrlInput(e.target.value)}
                  className="input-field pr-12"
                  placeholder="https://example.com/news-article or search for: climate change, election news, etc."
                  required
                  disabled={loading}
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                  {urlInput && !loading ? (
                    <button
                      type="button"
                      onClick={() => setUrlInput("")}
                      className="h-5 w-5 text-neutral-400 hover:text-neutral-600 focus:outline-none focus:text-neutral-600 transition-colors duration-200"
                      aria-label="Clear search"
                    >
                      <svg
                        className="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  ) : (
                    <svg
                      className="h-5 w-5 text-neutral-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  )}
                </div>
              </div>
              <p className="text-xs text-neutral-500 mt-1">
                Enter a news article URL to analyze its bias, or search for any
                topic to find related articles
              </p>
            </div>

            {/* Opposing Viewpoints Filter Toggle */}
            <div className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg border border-neutral-200">
              <div className="flex items-center space-x-3">
                <svg
                  className="h-5 w-5 text-neutral-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 4V2a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v2m3 0H4a2 2 0 0 0-2 2v1a1 1 0 0 0 1 1h18a1 1 0 0 0 1-1V6a2 2 0 0 0-2-2zM6 20h12a2 2 0 0 0 2-2v-6H4v6a2 2 0 0 0 2 2z"
                  />
                </svg>
                <div>
                  <h3 className="text-sm font-medium text-neutral-900">
                    Show Opposing Viewpoints Only
                  </h3>
                  <p className="text-xs text-neutral-600">
                    Filter suggested articles to only show different political
                    perspectives
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  const newState = !opposingViewpointsEnabled;
                  setOpposingViewpointsEnabled(newState);
                  balanceAppTracking.trackOpposingViewpointsToggle(newState);
                }}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                  opposingViewpointsEnabled
                    ? "bg-primary-600"
                    : "bg-neutral-200"
                }`}
                role="switch"
                aria-checked={opposingViewpointsEnabled}
                aria-labelledby="opposing-viewpoints-label"
              >
                <span
                  aria-hidden="true"
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    opposingViewpointsEnabled
                      ? "translate-x-5"
                      : "translate-x-0"
                  }`}
                />
              </button>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full flex items-center justify-center space-x-2"
            >
              {loading ? (
                <>
                  <div className="spinner"></div>
                  <span>Analyzing...</span>
                </>
              ) : (
                <>
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 10V3L4 14h7v7l9-11h-7z"
                    />
                  </svg>
                  <span>Analyze Article</span>
                </>
              )}
            </button>
          </form>
        </div>

        {/* Bias Analysis Results */}
        {renderBiasInfo()}

        {/* Suggested Articles */}
        {renderSuggestedArticles()}

        {/* Message Display */}
        {message && (
          <div className="bg-white rounded-xl shadow-lg border border-neutral-200 p-6">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-blue-400 mt-0.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <p className="text-sm text-neutral-700">{message}</p>
                <div className="mt-3">
                  <button
                    onClick={() => setMessage("")}
                    className="text-sm text-blue-600 hover:text-blue-500 font-medium"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* URL check examples */}
        <div className="bg-white rounded-xl shadow-lg border border-neutral-200 p-6">
          <h3 className="text-lg font-medium text-neutral-900 mb-4">
            Try These Examples
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <h4 className="font-medium text-neutral-900">URLs to analyze:</h4>
              <div className="space-y-1">
                {["cnn.com", "foxnews.com", "reuters.com", "nytimes.com"].map(
                  (domain) => (
                    <button
                      key={domain}
                      onClick={() =>
                        setUrlInput(`https://${domain}/some-article`)
                      }
                      className="block text-sm text-blue-600 hover:text-blue-500 text-left"
                    >
                      https://{domain}/some-article
                    </button>
                  )
                )}
              </div>
            </div>
            <div className="space-y-2">
              <h4 className="font-medium text-neutral-900">
                Topics to search:
              </h4>
              <div className="space-y-1">
                {[
                  "climate change news",
                  "election coverage",
                  "economic policy",
                  "healthcare debate",
                ].map((topic) => (
                  <button
                    key={topic}
                    onClick={() => setUrlInput(topic)}
                    className="block text-sm text-blue-600 hover:text-blue-500 text-left"
                  >
                    {topic}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

MainApp.propTypes = {
  user: PropTypes.object,
  error: PropTypes.string,
  setError: PropTypes.func.isRequired,
  urlInput: PropTypes.string.isRequired,
  setUrlInput: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  opposingViewpointsEnabled: PropTypes.bool.isRequired,
  setOpposingViewpointsEnabled: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  renderBiasInfo: PropTypes.func.isRequired,
  renderSuggestedArticles: PropTypes.func.isRequired,
  message: PropTypes.string,
  setMessage: PropTypes.func.isRequired,
  renderUserInfo: PropTypes.func.isRequired,
};

export default MainApp;
